<template>
    <section
        ref="section"
        class="projects col-center pl-10 pr-3">
        <div class="projects__stage col-center">
            <transition name="stack-fade">
                <ProjectSlide
                    :key="index"
                    :project="projects[index]" />
            </transition>
        </div>
        <!-- One dot per project; clicking one stops the auto-cycle. -->
        <div class="projects__pager">
            <v-icon
                v-for="(project, i) in projects"
                :key="project.title"
                class="ma-1"
                small
                :color="i === index ? 'yellow' : 'secondary'"
                :aria-label="project.title"
                @click="select(i)">
                {{ i === index ? 'mdi-circle' : 'mdi-circle-outline' }}
            </v-icon>
        </div>
    </section>
</template>

<script>
import { ref } from 'vue';
import { projects } from '@/data/projects';
import { useAutoCycle } from '@/composables/useAutoCycle';
import { useCodeScreenFade } from '@/composables/useCodeScreenSight';
import ProjectSlide from './ProjectSlide.vue';

export default {
    name: 'ProjectsSection',
    components: { ProjectSlide },
    setup() {
        const section = ref(null);
        // Only cycles while the section is on screen, so a slide never switches while it is still typing in.
        const { index, select } = useAutoCycle(projects.length, 5000, section);
        // The code screen fades out as the section scrolls in, so it doesn't show through its veil and the footer.
        useCodeScreenFade(section);

        return { section, projects, index, select };
    },
};
</script>

<style scoped>
.projects {
    min-height: 100vh;
    position: relative;
    z-index: 0; /* keeps the veil behind the slides */
}
/* Veil between the background and the slides: blurs the code screen and the network behind,
   fading out towards the footer. */
.projects::before {
    --veil-blur: 10px;
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    backdrop-filter: blur(var(--veil-blur));
    mask-image: linear-gradient(to bottom, black 75%, transparent);
    pointer-events: none;
}
/* Twilight: the veil is also a faint black gradient. Daylight's dark text needs the bright backdrop. */
[data-theme='twilight'] .projects::before {
    background-image: linear-gradient(to bottom, rgb(0 0 0 / 45%), rgb(0 0 0 / 15%));
}
.projects__stage {
    width: 100%;
    position: relative;
}
.projects__pager {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: absolute;
    left: 10px;
    top: 50%;
}
</style>
